/**
 * An app-wide "are you sure you want to leave?" for screens that must not be
 * left casually — today, the import wizard while an import runs (§80: "cannot
 * navigate away during import").
 *
 * ── Why not React Router's `useBlocker` ────────────────────────────────────
 * It works only under a data router (`createBrowserRouter`). This app mounts a
 * plain `<BrowserRouter>`, where `useBlocker` throws. Moving the whole app to a
 * data router for one guard is not a change to make in passing, so the guard
 * sits where the app's own navigation already funnels: `DispatchShell`'s
 * `handleNavigate` (the sidebar and the catalog-changes banner) and sign-out
 * call `confirmNavigation()` first. A reload, a closed tab and a typed URL are
 * covered by the browser's own `beforeunload` prompt, which the hook arms.
 *
 * The browser's Back button is NOT intercepted — `popstate` fires after the
 * URL has already changed. The import screen does not depend on it being
 * blocked: the import runs on the server either way, and the wizard re-attaches
 * to it on return (`ImportWizard`'s remembered batch).
 *
 * One guard at a time. Two screens holding a guard at once is not a case this
 * app has, and the last one armed winning is a reasonable answer if it ever is.
 */

import * as React from 'react';

let activeMessage: string | null = null;

/** Arm (a message) or disarm (null) the guard. Exported for tests. */
export function setNavigationGuard(message: string | null): void {
  activeMessage = message;
}

/**
 * True when navigation may go ahead: no guard is armed, or the user confirmed
 * leaving. Call it before any in-app navigation away from the current screen.
 */
export function confirmNavigation(): boolean {
  if (activeMessage === null) return true;
  return window.confirm(activeMessage);
}

/**
 * Hold the guard while `message` is non-null. Also arms the browser's own
 * leave-page prompt (whose text browsers no longer let a page choose).
 */
export function useNavigationGuard(message: string | null): void {
  React.useEffect(() => {
    if (message === null) return undefined;
    setNavigationGuard(message);
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return (): void => {
      // Only clear our own message — never one another screen armed since.
      if (activeMessage === message) setNavigationGuard(null);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [message]);
}
